import { useState } from 'react';
import { FinancePageLayout } from '@/components/finance';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Search, 
  Users, 
  GitMerge, 
  Archive, 
  Trash2,
  Download,
  CheckCircle2,
  XCircle,
  Loader2,
  Mail,
  MailX,
  UserX
} from 'lucide-react';
import { useStripeCleanup } from '@/hooks/useStripeCleanup';
import { DuplicatesTable } from '@/components/finance/stripe-cleanup/DuplicatesTable';
import { GuestMatchingSection } from '@/components/finance/stripe-cleanup/GuestMatchingSection';

export default function StripeCleanup() {
  const {
    // Duplicates
    isAnalyzing,
    isExecuting,
    progress,
    progressText,
    duplicates,
    summary,
    executionResults,
    selectedEmails,
    customersBreakdown,
    analyzeAll,
    executeMerge,
    toggleSelection,
    selectAll,
    clearSelection,
    // Guests
    isAnalyzingGuests,
    isExecutingGuests,
    guestProgress,
    guestProgressText,
    guestMatches,
    unmatchedGuests,
    guestSummary,
    selectedGuestIds,
    analyzeGuests,
    executeGuestMerge,
    toggleGuestSelection,
    selectAllGuests,
    clearGuestSelection,
    // Common
    exportReport,
  } = useStripeCleanup();

  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [showMergeAllDialog, setShowMergeAllDialog] = useState(false);

  const handleMergeSelected = () => {
    setShowMergeDialog(false);
    executeMerge(Array.from(selectedEmails));
  };

  const handleMergeAll = () => {
    setShowMergeAllDialog(false);
    executeMerge(duplicates.map(d => d.email));
  };

  return (
    <FinancePageLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Limpeza de Duplicados - Stripe</h1>
            <p className="text-muted-foreground mt-1">
              Identifique e unifique clientes duplicados no Stripe
            </p>
          </div>
          {(summary || guestSummary) && (
            <Button 
              variant="outline" 
              onClick={exportReport}
              disabled={isAnalyzing || isExecuting || isAnalyzingGuests}
            >
              <Download className="h-4 w-4 mr-2" />
              Exportar Relatório
            </Button>
          )}
        </div>

        {/* Customers Breakdown */}
        {customersBreakdown && (
          <Card className="bg-muted/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Visão Geral dos Customers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Primary stats */}
              <div className="grid gap-4 md:grid-cols-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                    <Users className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-lg font-bold">{customersBreakdown.total_from_api}</p>
                    <p className="text-xs text-muted-foreground">Total Stripe</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400">
                    <Users className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-lg font-bold">{customersBreakdown.active}</p>
                    <p className="text-xs text-muted-foreground">Ativos</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">
                    <Trash2 className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-lg font-bold">{customersBreakdown.deleted}</p>
                    <p className="text-xs text-muted-foreground">Deletados</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">
                    <Mail className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-lg font-bold">{customersBreakdown.with_email}</p>
                    <p className="text-xs text-muted-foreground">Com Email</p>
                  </div>
                </div>
              </div>

              {/* Detailed breakdown */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Detalhamento (apenas ativos)</p>
                <div className="grid gap-3 md:grid-cols-2 text-sm">
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Customers Reais:</span>
                      <span className="font-medium">{customersBreakdown.real_customers}</span>
                    </div>
                    <div className="flex justify-between pl-4">
                      <span className="text-muted-foreground">• Com email:</span>
                      <span>{customersBreakdown.real_with_email}</span>
                    </div>
                    <div className="flex justify-between pl-4">
                      <span className="text-muted-foreground">• Sem email:</span>
                      <span>{customersBreakdown.real_without_email}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Guests:</span>
                      <span className="font-medium">{customersBreakdown.guests}</span>
                    </div>
                    <div className="flex justify-between pl-4">
                      <span className="text-muted-foreground">• Com email:</span>
                      <span>{customersBreakdown.guests_with_email}</span>
                    </div>
                    <div className="flex justify-between pl-4">
                      <span className="text-muted-foreground">• Sem email:</span>
                      <span>{customersBreakdown.guests_without_email}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="duplicates" className="space-y-6">
          <TabsList>
            <TabsTrigger value="duplicates">Duplicados por Email</TabsTrigger>
            <TabsTrigger value="guests">Guests Órfãos</TabsTrigger>
          </TabsList>

          {/* Duplicates Tab */}
          <TabsContent value="duplicates" className="space-y-6">
            {/* Analysis Section */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Análise de Duplicados</CardTitle>
                <CardDescription>
                  Analise todos os clientes do Stripe para identificar duplicados por email
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <Button 
                    onClick={analyzeAll} 
                    disabled={isAnalyzing || isExecuting}
                    size="lg"
                  >
                    {isAnalyzing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Analisando...
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-2" />
                        Analisar Duplicados
                      </>
                    )}
                  </Button>
                </div>

                {isAnalyzing && (
                  <div className="space-y-2">
                    <Progress value={progress} className="h-2" />
                    <p className="text-sm text-muted-foreground">{progressText}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Summary Cards */}
            {summary && (
              <div className="grid gap-4 md:grid-cols-5">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                        <Users className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{summary.totalCustomers}</p>
                        <p className="text-xs text-muted-foreground">Com Email</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400">
                        <GitMerge className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{summary.duplicatesFound}</p>
                        <p className="text-xs text-muted-foreground">Grupos Duplicados</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400">
                        <GitMerge className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{summary.toMerge}</p>
                        <p className="text-xs text-muted-foreground">Para Merge</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400">
                        <Archive className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{summary.toArchive}</p>
                        <p className="text-xs text-muted-foreground">Para Arquivar</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">
                        <Trash2 className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{summary.toDelete}</p>
                        <p className="text-xs text-muted-foreground">Para Deletar</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Execution Results */}
            {executionResults.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Resultados da Execução</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {executionResults.map((result, index) => (
                      <div 
                        key={index}
                        className={`flex items-center gap-2 p-2 rounded-lg ${
                          result.success 
                            ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' 
                            : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                        }`}
                      >
                        {result.success ? (
                          <CheckCircle2 className="h-4 w-4" />
                        ) : (
                          <XCircle className="h-4 w-4" />
                        )}
                        <span className="font-mono text-sm">{result.email}</span>
                        {result.error && (
                          <span className="text-xs ml-auto">{result.error}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Duplicates Table */}
            {duplicates.length > 0 && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Duplicados Encontrados</CardTitle>
                    <CardDescription>
                      {duplicates.length} grupos de duplicados • {selectedEmails.size} selecionados
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={selectedEmails.size === duplicates.length ? clearSelection : selectAll}
                    >
                      {selectedEmails.size === duplicates.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowMergeDialog(true)}
                      disabled={selectedEmails.size === 0 || isExecuting}
                    >
                      Executar Selecionados ({selectedEmails.size})
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowMergeAllDialog(true)}
                      disabled={duplicates.length === 0 || isExecuting}
                    >
                      {isExecuting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Executando...
                        </>
                      ) : (
                        'Executar Todos'
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <DuplicatesTable
                    duplicates={duplicates}
                    selectedEmails={selectedEmails}
                    executionResults={executionResults}
                    onToggleSelection={toggleSelection}
                  />
                </CardContent>
              </Card>
            )}

            {/* Empty State */}
            {!isAnalyzing && !summary && (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Search className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium mb-2">Nenhuma análise realizada</h3>
                  <p className="text-muted-foreground text-sm text-center max-w-md mb-4">
                    Clique em "Analisar Duplicados" para identificar clientes com o mesmo email no Stripe
                  </p>
                </CardContent>
              </Card>
            )}

            {/* No Duplicates Found */}
            {summary && duplicates.length === 0 && (
              <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400 mb-4" />
                  <h3 className="text-lg font-medium mb-2 text-green-700 dark:text-green-300">
                    Nenhum duplicado encontrado!
                  </h3>
                  <p className="text-green-600 dark:text-green-400 text-sm text-center max-w-md">
                    Todos os {summary.totalCustomers} clientes com email do Stripe são únicos.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Guests Tab */}
          <TabsContent value="guests">
            <GuestMatchingSection
              isAnalyzing={isAnalyzingGuests}
              isExecuting={isExecutingGuests}
              progress={guestProgress}
              progressText={guestProgressText}
              guestMatches={guestMatches}
              unmatchedGuests={unmatchedGuests}
              guestSummary={guestSummary}
              selectedGuestIds={selectedGuestIds}
              onAnalyze={analyzeGuests}
              onExecuteMerge={executeGuestMerge}
              onToggleSelection={toggleGuestSelection}
              onSelectAll={selectAllGuests}
              onClearSelection={clearGuestSelection}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Merge Selected Dialog */}
      <AlertDialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Merge</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja executar o merge de {selectedEmails.size} grupos de duplicados?
              Esta ação irá mover métodos de pagamento e arquivar/deletar os duplicados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleMergeSelected} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Executar Merge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Merge All Dialog */}
      <AlertDialog open={showMergeAllDialog} onOpenChange={setShowMergeAllDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">⚠️ Atenção: Ação Irreversível</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Você está prestes a executar o merge de <strong>TODOS</strong> os {duplicates.length} grupos de duplicados.
              </p>
              <p>
                Isso irá:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Mover métodos de pagamento para os clientes principais</li>
                <li>Arquivar {summary?.toArchive || 0} clientes duplicados</li>
                <li>Deletar {summary?.toDelete || 0} clientes sem histórico</li>
              </ul>
              <p className="font-medium text-destructive">
                Esta ação NÃO pode ser desfeita!
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleMergeAll} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Sim, Executar Todos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FinancePageLayout>
  );
}
